import { Link, useNavigate } from "react-router-dom";
import { Activity, Check, Clock, Database, GitBranch, Send, X } from "lucide-react";
import { CAMPAIGNS, RNCS, WORKFLOWS, resolveWorkflow } from "@/pages/signals/rnc/rncData";
import { RncShell, ChannelBadge } from "@/pages/signals/rnc/RncShared";

export default function WorkflowAnalyticsPage() {
  const navigate = useNavigate();
  const totalWorkflows = Object.keys(WORKFLOWS).length;
  const running = RNCS.filter((r) => !["Qualified", "Unqualified"].includes(r.status)).length;
  const qualified = RNCS.filter((r) => r.status === "Qualified").length;
  const unqualified = RNCS.filter((r) => r.status === "Unqualified").length;
  const sql = RNCS.filter((r) => r.sqlConverted).length;
  const crm = RNCS.filter((r) => r.crmPushed).length;

  return (
    <RncShell
      crumb={(
        <>
          <Link to="/outreach/campaigns" className="link" style={{ color: "inherit", textDecoration: "none" }}>Outreach</Link>
          <span className="sep">/</span>
          <b>Workflow Analytics</b>
        </>
      )}
    >
      <div className="page-head">
        <div>
          <h1>Workflow Analytics</h1>
          <p>Performance across every campaign, workflow, and channel.</p>
        </div>
        <button type="button" className="btn" onClick={() => navigate("/outreach/campaigns")}>
          <Send size={15} /> Back to Campaigns
        </button>
      </div>

      <div className="stat-cards">
        <div className="stat"><div className="top"><span className="lbl">Total Workflows</span><div className="ic2" style={{ background: "var(--indigo-50)", color: "var(--indigo)" }}><GitBranch size={16} /></div></div><div className="num">{totalWorkflows}</div></div>
        <div className="stat"><div className="top"><span className="lbl">Running RNCs</span><div className="ic2" style={{ background: "var(--amber-50)", color: "var(--amber-600)" }}><Clock size={16} /></div></div><div className="num">{running}</div></div>
        <div className="stat"><div className="top"><span className="lbl">Qualified</span><div className="ic2" style={{ background: "var(--green-50)", color: "var(--green-600)" }}><Check size={16} strokeWidth={3} /></div></div><div className="num">{qualified}</div></div>
        <div className="stat"><div className="top"><span className="lbl">Unqualified</span><div className="ic2" style={{ background: "var(--red-50)", color: "var(--red-600)" }}><X size={16} strokeWidth={3} /></div></div><div className="num">{unqualified}</div></div>
      </div>
      <div className="stat-cards">
        <div className="stat"><div className="top"><span className="lbl">SQL Generated</span><div className="ic2" style={{ background: "var(--indigo-50)", color: "var(--indigo)" }}><Check size={16} /></div></div><div className="num">{sql}</div></div>
        <div className="stat"><div className="top"><span className="lbl">CRM Synced</span><div className="ic2" style={{ background: "var(--indigo-50)", color: "var(--indigo)" }}><Database size={16} /></div></div><div className="num">{crm}</div></div>
        <div className="stat"><div className="top"><span className="lbl">Avg Completion Time</span><div className="ic2" style={{ background: "var(--slate-100)", color: "var(--slate-600)" }}><Clock size={16} /></div></div><div className="num">2.6d</div></div>
        <div className="stat"><div className="top"><span className="lbl">Conversion %</span><div className="ic2" style={{ background: "var(--green-50)", color: "var(--green-600)" }}><Activity size={16} /></div></div><div className="num">{Math.round((qualified / RNCS.length) * 100)}%</div></div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "20px 22px 4px" }}><h3 style={{ margin: 0 }}>Workflow Performance</h3></div>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>CAMPAIGN</th><th>WORKFLOW</th><th>CHANNELS</th><th>RUNNING</th>
                <th>QUALIFIED</th><th>SQL</th><th>CRM</th><th>CONVERSION</th><th>AVG TIME</th>
              </tr>
            </thead>
            <tbody>
              {CAMPAIGNS.map((c) => {
                const { workflow: wf } = resolveWorkflow(c.workflowId);
                const list = RNCS.filter((r) => r.campaignId === c.id);
                const run = list.filter((r) => !["Qualified", "Unqualified"].includes(r.status)).length;
                const qual = list.filter((r) => r.status === "Qualified").length;
                const sqlN = list.filter((r) => r.sqlConverted).length;
                const crmN = list.filter((r) => r.crmPushed).length;
                const conv = list.length ? `${Math.round((qual / list.length) * 100)}%` : "—";
                return (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 700 }}>{c.name}</td>
                    <td>{wf?.name || "No Workflow Assigned"} <span style={{ color: "var(--slate-400)", fontSize: 12 }}>{c.version}</span></td>
                    <td style={{ display: "flex", gap: 4 }}>{c.channels.map((ch) => <ChannelBadge key={ch} channelKey={ch} size={20} />)}</td>
                    <td>{run}</td><td>{qual}</td><td>{sqlN}</td><td>{crmN}</td>
                    <td><span style={{ color: "var(--green-600)", fontWeight: 700 }}>{conv}</span></td>
                    <td>2.4 days</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </RncShell>
  );
}
